export const STEP_MS = 2560;
export const STEP_COUNT = 4;
export const SLIDE_MS = STEP_MS * STEP_COUNT;
export const EXIT_MS = 280;
export const ENTER_MS = 380;
export const TICK_MS = 80;

export type HeroPhase = "enter" | "play" | "exit";
export type HeroSlide = 0 | 1;

export interface HeroTimelineState {
  activeSlide: HeroSlide;
  phase: HeroPhase;
  elapsed: number;
  cycleId: number;
}

export const initialHeroTimeline = (reduced: boolean): HeroTimelineState => ({
  activeSlide: 0,
  phase: reduced ? "play" : "enter",
  elapsed: 0,
  cycleId: 0,
});

export function deriveStage(elapsed: number): { stage: number; p: number } {
  if (elapsed >= SLIDE_MS) return { stage: STEP_COUNT - 1, p: 1 };
  const stage = Math.min(STEP_COUNT - 1, Math.floor(elapsed / STEP_MS));
  const p = (elapsed % STEP_MS) / STEP_MS;
  return { stage, p };
}

export function tenderCounters(stage: number, p: number): { bids: number; views: number } {
  if (stage <= 0) return { bids: 0, views: Math.round(40 * p) };
  if (stage === 1) return { bids: Math.round(12 * p), views: Math.round(40 + 72 * p) };
  return { bids: 12, views: 112 };
}

export function contractBar(stage: number, p: number): number {
  return stage >= STEP_COUNT - 1 ? p : 0;
}

export function tabProgress(phase: HeroPhase, elapsed: number): number {
  if (phase !== "play") return 0;
  return Math.min(elapsed / SLIDE_MS, 1);
}

export function tickElapsed(state: HeroTimelineState, reduced: boolean): HeroTimelineState {
  if (reduced || state.phase !== "play") return state;
  const next = state.elapsed + TICK_MS;
  if (next >= SLIDE_MS) return { ...state, elapsed: SLIDE_MS, phase: "exit" };
  return { ...state, elapsed: next };
}

export function requestSlide(state: HeroTimelineState, index: HeroSlide, reduced: boolean): HeroTimelineState {
  if (state.activeSlide === index) return state;
  if (reduced) {
    return { activeSlide: index, phase: "play", elapsed: 0, cycleId: state.cycleId + 1 };
  }
  if (state.phase !== "play") return state;
  return { ...state, phase: "exit" };
}

export function requestStep(state: HeroTimelineState, index: number): HeroTimelineState {
  if (state.phase !== "play") return state;
  const step = Math.min(STEP_COUNT - 1, Math.max(0, index));
  return { ...state, elapsed: step * STEP_MS };
}

export function finishExit(state: HeroTimelineState, reduced: boolean): HeroTimelineState {
  if (state.phase !== "exit") return state;
  return {
    activeSlide: state.activeSlide === 0 ? 1 : 0,
    phase: reduced ? "play" : "enter",
    elapsed: 0,
    cycleId: state.cycleId + 1,
  };
}

export function finishEnter(state: HeroTimelineState): HeroTimelineState {
  if (state.phase !== "enter") return state;
  return { ...state, phase: "play", elapsed: 0 };
}

export function assertHeroTimeline(): void {
  const opened = deriveStage(0);
  if (opened.stage !== 0 || opened.p !== 0 || tenderCounters(opened.stage, opened.p).bids !== 0) {
    throw new Error("hero timeline: a new slide must start at stage 0 with 0 bids");
  }

  const visited: string[] = [];
  let state = initialHeroTimeline(false);
  const guard = STEP_COUNT * 3 * (SLIDE_MS / TICK_MS + 2);
  for (let n = 0; n < guard; n += 1) {
    const before = `${state.activeSlide}:${deriveStage(state.elapsed).stage}`;
    if (state.phase === "play" && (visited.length === 0 || visited[visited.length - 1] !== before)) {
      visited.push(before);
    }
    if (state.phase === "play") state = tickElapsed(state, false);
    else if (state.phase === "exit") state = finishExit(state, false);
    else state = finishEnter(state);

    if (state.phase === "enter") {
      const fresh = deriveStage(state.elapsed);
      if (fresh.stage !== 0 || fresh.p !== 0 || tenderCounters(fresh.stage, fresh.p).bids !== 0) {
        throw new Error("hero timeline: swap did not reset the incoming slide");
      }
      if (contractBar(fresh.stage, fresh.p) !== 0) {
        throw new Error("hero timeline: contract bar must be empty at stage 0");
      }
    }
    if (visited.length >= STEP_COUNT * 3) break;
  }

  const expected = ["0:0", "0:1", "0:2", "0:3", "1:0", "1:1", "1:2", "1:3", "0:0", "0:1", "0:2", "0:3"];
  if (visited.join(",") !== expected.join(",")) {
    throw new Error(`hero timeline order ${visited.join(",")}`);
  }
  if (contractBar(3, 1) !== 1 || contractBar(2, 1) !== 0) {
    throw new Error("hero timeline: contract bar fills only on step 4");
  }
}

if (process.env.NODE_ENV !== "production") {
  assertHeroTimeline();
}
